// Pure-typed-array 2-layer rasterizer (architecture.md §19.1).
//
// This is the "drawing core" — no DOM types are referenced anywhere in
// this file, so it runs identically in:
//
//   - the browser main thread (HTMLCanvasElement-backed engine)
//   - a dedicated Web Worker with OffscreenCanvas
//   - Node's `vitest` test runner (no canvas at all)
//   - the Phase 5 server-side snapshot worker (`@napi-rs/canvas`)
//
// Pixel layout: `Uint8ClampedArray` of length `width*height*4` in RGBA
// order, matching `ImageData.data`. The "main canvas" buffer holds
// committed pixels; the per-stroke "stroke buffer" accumulates alpha for
// the in-progress stroke so the per-segment sweep produces the
// "中央濃く外周薄く" feel without artifacts.
//
// 2026-05-09 (consult / brush-alpha-cap-investigation.md §4-I + §5):
//
// The stroke buffer is stored as **Uint16Array (8.8 fixed-point)** over a
// **bbox-local sub-buffer** (= NOT the full canvas). The buffer accumulates
// per-stamp alpha via **source-over (漸近合成)** rather than the old
// saturating-add. This solves the "太さ + alpha 中域で完全飽和" problem the
// 25-year sat-add approach hit on the new larger canvas (1500² + 太さ 60),
// while preserving the "厚塗り" culture (= 同 stroke 内ぐりぐり build-up
// that grows asymptotically toward 100% but never caps mid-stroke).
//
// Invariants (DO NOT BREAK without re-running the consult):
//
//   - **per-stroke buffer is transient**: allocated in `createStrokeBuffer`,
//     freed when `endStroke` calls `clearStrokeBuffer` + drops the sid.
//     There is NO cross-stroke accumulator. The persistent layer (Uint8)
//     holds committed pixels.
//
//   - **buffer is local to the stroke's bbox + stamp-radius padding**, not
//     the full canvas. This keeps multi-user memory bounded (10 users × ~13
//     MB total, vs ~180 MB for full-canvas Uint16 buffers — see
//     brush-alpha-cap-investigation.md §4-I).
//
//   - **bbox grows by 1.5× steps when a stamp falls outside**, with the
//     buffer typed-array reallocated + copied via `set()`. Growth is
//     monotonic within a stroke — we never shrink mid-stroke.
//
//   - **per-stamp alpha is float**: `K * pow(strokeAlpha/255, gamma)`. We
//     quantise to 8.8 fixed-point for the source-over accumulation:
//
//       per_stamp_fixed = round(per_stamp * 256)        // 0..K*256
//       buffer[i] += (per_stamp_fixed * (65535 - buffer[i])) >> 16
//
//     The shift-by-16 approximates a `/65535` ÷-by-65536 with a max error
//     of ~1 part in 65535 (< 0.002% — invisible at 8-bit final output).
//
//   - **commit converts Uint16 → Uint8 only over the bbox** (not the full
//     canvas) and alpha-blends onto `layer.main` exactly once per
//     commit-point (segment for `brush`, end-of-stroke for `flat`).
//
// Phase 5 note: this module lives in `@oekaki/drawing-core` so the server
// snapshot worker (`@napi-rs/canvas`) and the web client (Canvas2D /
// OffscreenCanvas) share bit-identical pixel results. The Uint16
// fixed-point + bbox machinery here is what guarantees that parity even
// when the underlying compositor is the JS engine vs the napi-rs binding.
// (architecture.md §11)
//
// (architecture.md §19.1 / §19.10 E)
import { getPenPattern } from './penPattern.js';
import { DRAWING_PERF_MEASURE_NAMES, measureDrawingHotPath } from './perfMeasure.js';
import { airbrushIsInkFast, prepareAirbrushScatterTable, } from './stamp/texture/airbrushHash.js';
import { directionFromSegment, NO_DIRECTION, sampleTexel, sampleTextureAlpha, } from './stamp/texture/textureSampler.js';
const allocPixels = (length) => new Uint8ClampedArray(new ArrayBuffer(length));
/** Allocate an empty layer (transparent). */
export const createLayer = (id, width, height) => ({
    id,
    width,
    height,
    main: allocPixels(width * height * 4),
});
/**
 * Canvas background color used by the mask compare fallback (D'' rule).
 *
 * Java の旧版は `imageSource[i] = 0xffffffff` で **白 α=255** で全塗り初期化
 * したうえで mask compare が走るため、α=0 (= 空) 領域も「白色」として一致判定
 * されていた. Node の透明初期化モデル (= `createLayer` で α=0) で Java 互換の
 * mask 挙動を再現するには、mask compare 時に **α=0 のセルを bg 色 (= 白) で
 * 置換してから比較する** 必要がある (= D'' rule).
 *
 * MVP は `0xFFFFFF` 固定. 将来 (β 以降) でキャンバス bg 色を設定可能にする
 * 余地はあるが、その時点で `StrokeRenderer` 経由でレンダラに伝える経路を
 * 追加する形になる (= レイヤーには bg は属さない方針、§5 of
 * mask-java-compat-2026-05-21.md).
 *
 * 兼用効果: 旧コードの「α=0 を一致から除外する」消しゴム跡対策最適化
 * (line 501-516, removed) と同じ結果を「α=0 → 白として比較」で自然に再現
 * (= 黒マスク + 黒線消し跡 → 白扱い → 黒 ≠ 白 → 描ける).
 */
export const CANVAS_BG_RGB = 0xffffff;
const NO_BBOX = () => ({ x0: -1, y0: -1, x1: -1, y1: -1 });
/**
 * Allocate an empty stroke buffer. The `alpha16` typed-array is created
 * lazily on the first stamp (= zero allocation if the stroke is cancelled
 * before any point lands).
 *
 * `width` / `height` are the layer dimensions used for clamping the bbox
 * during expansion; the buffer's actual `alpha16` allocation grows from
 * zero as stamps land.
 */
export const createStrokeBuffer = (width, height) => ({
    width,
    height,
    bbox: NO_BBOX(),
    dirty: NO_BBOX(),
    alpha16: new Uint16Array(0),
});
/**
 * Reset the buffer to its empty state. Drops the `alpha16` allocation so
 * the next stroke that reuses this object (currently we don't, but the
 * `endStroke → delete sid` pattern + GC means it doesn't matter) starts
 * fresh.
 */
export const clearStrokeBuffer = (buf) => {
    buf.bbox = NO_BBOX();
    buf.dirty = NO_BBOX();
    buf.alpha16 = new Uint16Array(0);
};
/**
 * Read the buffer's per-pixel alpha as a 0..255 integer at layer-space
 * `(x, y)`. Returns 0 for out-of-bbox cells. Used by tests + by the
 * commit path's mirror of "what the buffer holds for this pixel".
 *
 * The conversion is `round(value16 / 256)` clamped to 0..255 — bit-
 * identical to how `commitBufferToLayer` decimates 8.8 fixed-point down
 * to the 8-bit alpha used in the final source-over alpha-blend.
 */
export const readStrokeBufferAlpha = (buf, x, y) => {
    const { x0, y0, x1, y1 } = buf.bbox;
    if (x0 < 0 || x < x0 || x > x1 || y < y0 || y > y1)
        return 0;
    const bboxW = x1 - x0 + 1;
    const v16 = buf.alpha16[(y - y0) * bboxW + (x - x0)] ?? 0;
    // 8.8 → 8-bit (round-to-nearest, clamp).
    const v = Math.round(v16 / 256);
    return v > 255 ? 255 : v;
};
/**
 * BBox growth strategy (consult 2026-05-09 / brush-alpha-cap-investigation.md
 * §5 残課題 #3):
 *
 *   - Initial allocation: `(cx-r-pad, cy-r-pad)..(cx+r+pad, cy+r+pad)`
 *     where `r = ceil(penWidth / 2)` and `pad = STAMP_PADDING_PX` (= a
 *     small fixed margin so the *very next* stamp likely fits without
 *     triggering a regrow, even with sub-pixel motion).
 *
 *   - Expansion: when a stamp falls outside the current bbox, grow ONLY
 *     the side(s) that actually need to grow. Per dimension that needs
 *     expansion, the new extent is the **larger of**:
 *       (a) `union(cur, need)` extent on that side (= the strict minimum),
 *       (b) `union(cur, need) + 0.5 × current_dim_size` on that side
 *           (= 1.5× amortised growth, but only on the touched side, so
 *           the bbox doesn't expand into the *opposite* direction the
 *           stroke isn't drifting toward).
 *     Then clamp to layer bounds. The 1.5× factor preserves the consult-
 *     confirmed amortisation (= O(1) per stamp over a long stroke), but
 *     applies it directionally to keep the bbox *tight to the stroke
 *     trajectory*. Multi-user load tests showed that the previous
 *     "centered 1.5× expansion" strategy roughly doubled the buffer
 *     footprint (= bbox grew on both sides even when the stroke only
 *     drifted to the right), pushing 10-user totals to ~21 MB; the
 *     directional strategy lands closer to the consult §4-I 13 MB target.
 *
 *   - Reallocation: alloc a new `Uint16Array` at the new bbox dimensions,
 *     blit the old `alpha16` into the corresponding sub-rect via `.set()`
 *     (typed-array `.set()` is a single C-level memcpy per row), then
 *     replace the old typed-array reference. Old GCs harmlessly.
 *
 * `STAMP_PADDING_PX` is a small constant nudge (matching the legacy "+1
 * round-to-half-pixel" margin in `MemoryImageGraphics.drawLineReal`). The
 * 1.5× regrow is what amortises the cost of long strokes that drift
 * gradually across the canvas; the padding is what avoids a regrow on the
 * very second stamp of a slowly-moving stroke.
 */
const STAMP_PADDING_PX = 2;
/**
 * Amortised growth fraction added to the dimension on each touched side.
 * Per touched side: `new_dim_extent = union_extent + GROWTH × cur_dim`.
 * 0.5 (= 1.5× total per-side dim growth) is the consult-confirmed
 * amortisation factor (dynamic-array analysis: <1.0 avoids waste, >0
 * keeps amortised cost O(1) per stamp).
 */
const BBOX_GROWTH_FRACTION = 0.5;
/**
 * Compute the bbox of a stamp's footprint at `(cx, cy)` with `penWidth`,
 * clamped to the layer.
 */
const stampFootprintBbox = (cx, cy, penWidth, width, height) => {
    const half = Math.ceil(penWidth / 2) + 1;
    const fx = Math.floor(cx);
    const fy = Math.floor(cy);
    const x0 = Math.max(0, fx - half);
    const y0 = Math.max(0, fy - half);
    const x1 = Math.min(width - 1, fx + half);
    const y1 = Math.min(height - 1, fy + half);
    return { x0, y0, x1, y1 };
};
/**
 * Ensure `buf.bbox` covers `need`. If not, grow ONLY the touched sides
 * (directional 1.5× amortisation per side, clamped to layer bounds) and
 * rebind `buf.alpha16`. Returns `true` iff a regrow happened.
 *
 * Directional growth: a stroke drifting to the right pays growth only on
 * the right side; the left side stays put. This keeps the bbox tight to
 * the stroke trajectory (= ~50% smaller than the previous "centered 1.5×"
 * strategy under load tests).
 */
const ensureBboxCovers = (buf, need) => {
    const cur = buf.bbox;
    if (cur.x0 < 0) {
        // First allocation: take `need` directly + a tiny padding margin.
        const x0 = Math.max(0, need.x0 - STAMP_PADDING_PX);
        const y0 = Math.max(0, need.y0 - STAMP_PADDING_PX);
        const x1 = Math.min(buf.width - 1, need.x1 + STAMP_PADDING_PX);
        const y1 = Math.min(buf.height - 1, need.y1 + STAMP_PADDING_PX);
        buf.bbox = { x0, y0, x1, y1 };
        buf.alpha16 = new Uint16Array((x1 - x0 + 1) * (y1 - y0 + 1));
        return true;
    }
    if (need.x0 >= cur.x0 && need.y0 >= cur.y0 && need.x1 <= cur.x1 && need.y1 <= cur.y1) {
        return false; // already covered
    }
    // Per-side directional growth: the side that *needs* to grow gets the
    // amortisation padding; the side that doesn't need to grow stays put.
    // `growthW` / `growthH` are the per-side amortisation pads (computed
    // from the *current* bbox dim so amortisation cost is O(1) per stamp).
    const curW = cur.x1 - cur.x0 + 1;
    const curH = cur.y1 - cur.y0 + 1;
    const growthW = Math.ceil(curW * BBOX_GROWTH_FRACTION);
    const growthH = Math.ceil(curH * BBOX_GROWTH_FRACTION);
    // Start from the strict union; expand only sides that are outgrown.
    let nx0 = Math.min(cur.x0, need.x0);
    let ny0 = Math.min(cur.y0, need.y0);
    let nx1 = Math.max(cur.x1, need.x1);
    let ny1 = Math.max(cur.y1, need.y1);
    if (need.x0 < cur.x0)
        nx0 = Math.min(nx0, cur.x0 - growthW);
    if (need.y0 < cur.y0)
        ny0 = Math.min(ny0, cur.y0 - growthH);
    if (need.x1 > cur.x1)
        nx1 = Math.max(nx1, cur.x1 + growthW);
    if (need.y1 > cur.y1)
        ny1 = Math.max(ny1, cur.y1 + growthH);
    // Clamp to layer bounds (defensive: union floor is already non-negative
    // because both `cur` and `need` are themselves clamped).
    const fx0 = Math.max(0, nx0);
    const fy0 = Math.max(0, ny0);
    const fx1 = Math.min(buf.width - 1, nx1);
    const fy1 = Math.min(buf.height - 1, ny1);
    const oldBbox = cur;
    const oldAlpha = buf.alpha16;
    const oldW = oldBbox.x1 - oldBbox.x0 + 1;
    const newBbox = { x0: fx0, y0: fy0, x1: fx1, y1: fy1 };
    const newW = newBbox.x1 - newBbox.x0 + 1;
    const newH = newBbox.y1 - newBbox.y0 + 1;
    const newAlpha = new Uint16Array(newW * newH);
    // Blit the old buffer into the new one at its new offset. One row at a
    // time so we can use the typed-array C-level memcpy (subarray + set).
    const dx = oldBbox.x0 - newBbox.x0;
    const dy = oldBbox.y0 - newBbox.y0;
    const oldH = oldBbox.y1 - oldBbox.y0 + 1;
    for (let y = 0; y < oldH; y++) {
        const srcStart = y * oldW;
        const dstStart = (y + dy) * newW + dx;
        newAlpha.set(oldAlpha.subarray(srcStart, srcStart + oldW), dstStart);
    }
    buf.bbox = newBbox;
    buf.alpha16 = newAlpha;
    return true;
};
/**
 * Whether inclusive bboxes `a` / `b` overlap (touching edges count as
 * intersecting). Used ONLY by the `clip` write-gate below (Undo B' bbox-
 * local repair) to early-skip a whole stamp whose footprint doesn't
 * intersect the clip rect at all — before any buffer growth / mask /
 * texture work happens for it.
 */
const bboxOverlaps = (a, b) => a.x0 <= b.x1 && b.x0 <= a.x1 && a.y0 <= b.y1 && b.y0 <= a.y1;
/**
 * Crayon soft-edge width in threshold units (0..255), phase11-light-pens-
 * 2026-08-21.md §2.5.1. A texel margin (`densityLevel - t`) below this width
 * gets a per-stamp alpha proportional to `margin / WIDTH`; at/above it, the
 * texel inks at the full per-stamp alpha (identical to `tone` with
 * `aa=false`). This is the DEFAULT used when `StampParams.textureThresholdSoft
 * === true` but `textureThresholdSoftEdgeWidth` is omitted — every crayon call
 * site omits it (crayon always wants this width), so crayon's rendering is
 * unaffected by the §4.11 addition of a second, narrower width for tone.
 *
 * Fixed — not a protocol/stroke parameter — so it mirrors Rust's
 * `CRAYON_SOFT_EDGE_WIDTH` constant (`drawing-core-wasm/src/brush.rs`)
 * byte-for-byte; the two MUST stay numerically identical (no value crosses
 * the Wasm ABI for this — see that file's doc for why a duplicated-constant
 * pair, guarded by a parity test, is the deliberate choice here rather than
 * threading a new ABI parameter).
 */
// §2.5.2 (③ "ついでの調整", real-device pass): widened from 56 → 140. With the
// narrow 56 margin, only a thin sliver of texels near the cutoff got graded
// alpha — the vast majority of "inked" texels sat comfortably past the
// margin and rendered at the FULL per-stamp alpha, so a stroke's interior
// read as a flat solid fill with a few hard white gaps (binary), not the
// softer intensity variation the 2nd reference photo (`crayon-ref-
// procreate-2.jpg`) shows. Widening the margin to a bit over half the 0..255
// threshold range means MOST inked texels now fall inside the graded zone,
// so their alpha correlates with how far below the cutoff their raw
// wax-grain value sits — a continuous intensity gradient instead of a
// binary stamp.
const CRAYON_THRESHOLD_SOFT_EDGE_WIDTH = 200;
/**
 * Tone soft-edge width in threshold units (0..255), phase11-light-pens-
 * 2026-08-21.md §4.11. Passed as `StampParams.textureThresholdSoftEdgeWidth`
 * by `createToneStampRenderer` ONLY when the stroke's own `aa` is `true` (§4.11
 * point 2 — `aa=false` never sets `textureThresholdSoft` at all, so this
 * constant is unreachable for the binary path).
 *
 * Deliberately far narrower than crayon's 200: crayon wants "ほぼ全域が段階的"
 * (almost the whole grain gradated) while tone wants "縁 1px 程度だけ" (only
 * ~1px of feathering at each halftone dot's edge) — crayon's kernel softens
 * the interior texture itself, tone's is meant to only knock the hard-cutoff
 * jag off each dot's silhouette. Tuned against a real render at pitch 16/32,
 * density≈0.5 (`docs/_wip/_artifacts/phase11-light-pens/tone-aa.png`):
 * narrower widths under-fed the AA ramp per dot (only 1-2 texels graded, still
 * visibly stair-stepped at the dot's clustered-dot corner spread), and this
 * value produced a clean visible feather without noticeably shrinking the
 * dot's apparent size.
 *
 * Mirrors Rust's `TONE_SOFT_EDGE_WIDTH` constant (`drawing-core-wasm/src/
 * texture.rs`) byte-for-byte — same duplicated-constant-pair contract as
 * `CRAYON_THRESHOLD_SOFT_EDGE_WIDTH` above, guarded by a parity test.
 */
export const TONE_THRESHOLD_SOFT_EDGE_WIDTH = 28;
/**
 * Crayon-only radial density falloff (phase11-light-pens-2026-08-21.md
 * §2.5.2 "② 縁のほつれ"). The stroke's centre stays at full `densityLevel`;
 * near the stamp's own edge the EFFECTIVE comparison level is scaled down,
 * so fewer grain texels clear the ink test out there — the stroke reads as
 * dense in the middle and progressively sparser/fraying at the edge, instead
 * of a uniformly-dense disc with a hard silhouette.
 *
 * Deliberately reuses the SAME shape family `airbrushDensityLevelAt`
 * (`stamp/texture/airbrushHash.ts` §2.3) already established — `u = (r² -
 * d²) / r²` (1 at the centre, 0 at/beyond the stamp radius), **no `sqrt`, no
 * trig, no `Math.pow`** (only `+ - * /` + clamp, the same determinism class
 * `stampOnceAA`'s SDF coverage already relies on). Two differences from
 * airbrush's falloff, both because crayon's edge should fray, not vanish
 * (§2.5.2: "エアブラシほど強く落とすと線が消えるので、弱めから始める"):
 *
 *   - `u` is NOT raised to a steep exponent (airbrush uses `k=3` to fight a
 *     coverage-plateau problem specific to its many-dabs-per-pixel
 *     accumulation, `AIRBRUSH_FALLOFF_EXPONENT`'s doc) — crayon uses `k=1`
 *     (linear in `u`), the gentlest useful falloff.
 *   - the scaled level has a FLOOR (`CRAYON_EDGE_FALLOFF_MIN` fraction of
 *     `peakLevel`) rather than going to 0 at the edge — airbrush WANTS 0 at
 *     the edge (mist truly thins to nothing); crayon wants the edge merely
 *     THINNER, not erased, so the silhouette doesn't visibly shrink inward
 *     from the nominal pen width.
 *
 * `d2` is allowed to exceed `r2` (the AA coverage ramp keeps ~0.5px of
 * partial coverage just past the geometric radius) — `u` clamps to 0 there
 * rather than going negative, so the floor is what's used, not a cliff to 0.
 */
const CRAYON_EDGE_FALLOFF_MIN = 0.4;
const crayonEdgeDensityLevelAt = (dx, dy, r, peakLevel) => {
    const rr = r < 0.5 ? 0.5 : r;
    const r2 = rr * rr;
    const d2 = dx * dx + dy * dy;
    const uRaw = (r2 - d2) / r2;
    const u = uRaw < 0 ? 0 : uRaw > 1 ? 1 : uRaw;
    const factor = CRAYON_EDGE_FALLOFF_MIN + (1 - CRAYON_EDGE_FALLOFF_MIN) * u;
    return Math.floor(peakLevel * factor);
};
/**
 * Stamp the pen pattern centered on (x, y) into the stroke buffer. Pixels
 * failing the mask check are skipped. The buffer-accumulation rule is
 * picked per stamp (source-over for `brush`, max-set for `flat`), which
 * is the only per-pen difference at the kernel level. This is the inner
 * kernel that the future Wasm `StampRenderer` replaces.
 *
 * 2026-05-09 (consult): the buffer is bbox-local Uint16 (8.8 fixed-point);
 * we ensure the bbox covers the stamp footprint before writing, and write
 * via source-over (`'add'`) or max-set (`'max'`).
 */
const stampOnce = (p) => {
    // AA dispatch (modern-layout.md §9.9). `aa === true` routes to the
    // analytic-SDF coverage path; the default (`undefined`/`false`) keeps the
    // legacy binary-disc path below **byte-for-byte unchanged** (= Classic 無
    // 退行、回帰テストで固定). The AA branch is a strict superset only on the
    // input side — it never touches `penPattern.ts`.
    if (p.aa === true) {
        stampOnceAA(p);
        return;
    }
    const { buffer, mainRgba, maskSnapshot, width, height, x, y, mask, texture, airbrush, clip } = p;
    const textureDir = p.textureDir ?? NO_DIRECTION;
    const textureThreshold = p.textureThreshold === true;
    const densityLevel = p.densityLevel ?? 0;
    const textureThresholdSoft = p.textureThresholdSoft === true;
    const textureThresholdSoftEdgeWidth = p.textureThresholdSoftEdgeWidth ?? CRAYON_THRESHOLD_SOFT_EDGE_WIDTH;
    const textureThresholdEdgeFalloff = p.textureThresholdEdgeFalloff === true;
    const stampIndex = p.stampIndex ?? 0;
    const accumulate = p.accumulate ?? 'add';
    // Apply pressure scales. `widthScale` / `alphaScale` are already-mapped
    // multipliers in [0, 1] (architecture.md §19.15 contract — send-side
    // mapping, rasterizer reflects). `undefined` means "no pressure", which
    // is bit-identical to the pre-pressure path because we floor at width≥1
    // and round alpha unchanged when scale=1.
    const widthScale = p.widthScale ?? 1;
    const alphaScale = p.alphaScale ?? 1;
    const scaledWidthRaw = p.penWidth * widthScale;
    const scaledWidth = scaledWidthRaw < 1 ? 1 : Math.round(scaledWidthRaw);
    // Per-stamp alpha is float for `'brush'` (so the source-over accumulator
    // can use the full 8.8 fixed-point precision down to the 1/256 boundary
    // — no 床問題 where low strokeAlpha rounds to zero in the per-stamp
    // curve). For `'flat'` (max accumulator) the wire alpha is integer 0..255
    // and float arithmetic collapses to the same value. Quantisation to 8.8
    // happens below at the accumulator step.
    const scaledAlphaRaw = p.penAlpha * alphaScale;
    // Clamp 0..255 (as a float). `<= 0` early-out catches the pressure=0 case
    // for free; we keep the upper clamp at 255 because penAlpha is the
    // per-stamp opacity and values above 1 (in 0..1 space) are not
    // meaningful for source-over.
    const penAlpha = scaledAlphaRaw <= 0 ? 0 : scaledAlphaRaw >= 255 ? 255 : scaledAlphaRaw;
    if (penAlpha <= 0)
        return;
    const penWidth = scaledWidth;
    const pattern = getPenPattern(penWidth);
    const half = (pattern.size - 1) / 2;
    // Airbrush radial falloff radius (§2.3): uses the UNROUNDED scaled width
    // (matches the AA kernel's own radius, `stampOnceAA`'s `r`) so the falloff
    // shape is consistent between the Classic and Modern paths even though the
    // Classic pattern mask itself is a rounded-integer disc. Only computed when
    // an airbrush stroke is active (cheap either way — one divide + one clamp).
    const airbrushRadius = airbrush !== undefined ? Math.max(0.5, scaledWidthRaw / 2) : 0;
    // Crayon-only edge falloff radius (§2.5.2 "② 縁のほつれ", `crayonEdgeDensityLevelAt`'s
    // doc). Same formula/inputs as `airbrushRadius` (unrounded scaled width) —
    // only computed when the crayon-only falloff flag is set (§4.11 point 3:
    // tone's AA soft edge does NOT enable this, even though it does enable
    // `textureThresholdSoft`), cheap either way.
    const crayonRadius = textureThresholdEdgeFalloff ? Math.max(0.5, scaledWidthRaw / 2) : 0;
    // Round so even-sized patterns sit on the half-pixel grid the legacy
    // `MemoryImageGraphics` used, with the +1 nudge folded into half itself.
    const px = Math.floor(x - half);
    const py = Math.floor(y - half);
    // Ensure the buffer's bbox covers this stamp's footprint. May reallocate
    // `buffer.alpha16` (1.5× growth) and rebind it.
    const need = stampFootprintBbox(x, y, penWidth, width, height);
    if (need.x0 > need.x1 || need.y0 > need.y1)
        return; // stamp entirely off-canvas
    // Undo B' write clip: skip the ENTIRE stamp (no buffer growth, no mask /
    // texture work) when its footprint doesn't touch `clip` at all. This is
    // what keeps a long intersecting stroke's local repair cheap — most of its
    // stamps are nowhere near the small cancelled-stroke bbox.
    if (clip !== undefined && !bboxOverlaps(need, clip))
        return;
    ensureBboxCovers(buffer, need);
    const { x0: bx0, y0: by0, x1: bx1, y1: by1 } = buffer.bbox;
    const bboxW = bx1 - bx0 + 1;
    const alpha16 = buffer.alpha16;
    // 8.8 fixed-point per-stamp value. Source-over: `out = in + s * (1 - in)`
    // in 0..1 space; in 0..65535 space that becomes
    //   out = in + ((s * (65535 - in)) >> 16)
    // where s is the per-stamp value scaled into 0..65535 (= penAlpha * 256,
    // since penAlpha is a 0..255 *float* — for `'brush'` the per-stamp curve
    // returns floats like 5.3 at alpha=147; for `'flat'` it's an integer).
    // `Math.round` quantises only at this fixed-point boundary (= 8.8
    // precision retained across all inputs).
    const penAlpha16 = Math.round(penAlpha * 256); // 0..65280
    const maskActive = mask.type !== 'none';
    const maskColors = mask.colors;
    const maskInverse = mask.type === 'inverse';
    // Airbrush scatter kernel (grain>=1) perf: precompute this stamp's cell
    // droplets ONCE (`prepareAirbrushScatterTable`'s doc) instead of re-hashing
    // per pixel. Cell range = the pixel footprint this loop will actually
    // iterate (== the same [0,width)/[bx0,bx1]/clip clamps the loop below
    // applies per-row/per-col), padded by the ±1 cell neighbour margin
    // `airbrushScatterInk` proves sufficient. `grain === 0` needs no table
    // (already a single hash per pixel, unchanged).
    const airbrushScatter = airbrush !== undefined && airbrush.grain > 0;
    if (airbrushScatter) {
        const grain = airbrush.grain;
        const nyLo = Math.max(0, by0, py, clip !== undefined ? clip.y0 : 0);
        const nyHi = Math.min(height - 1, by1, py + pattern.size - 1, clip !== undefined ? clip.y1 : height - 1);
        const nxLo = Math.max(0, bx0, px, clip !== undefined ? clip.x0 : 0);
        const nxHi = Math.min(width - 1, bx1, px + pattern.size - 1, clip !== undefined ? clip.x1 : width - 1);
        if (nyLo <= nyHi && nxLo <= nxHi) {
            prepareAirbrushScatterTable(airbrush, stampIndex, x, y, airbrushRadius, (nxLo >> grain) - 1, (nyLo >> grain) - 1, (nxHi >> grain) + 1, (nyHi >> grain) + 1);
        }
    }
    for (let ay = 0; ay < pattern.size; ay++) {
        const ny = py + ay;
        if (ny < 0 || ny >= height)
            continue;
        if (ny < by0 || ny > by1)
            continue; // outside bbox (shouldn't happen post-ensure but defensive)
        if (clip !== undefined && (ny < clip.y0 || ny > clip.y1))
            continue; // whole row outside the write clip
        const patRow = ay * pattern.size;
        const layerRow = ny * width;
        const bufRow = (ny - by0) * bboxW;
        for (let ax = 0; ax < pattern.size; ax++) {
            if (pattern.mask[patRow + ax] === 0)
                continue;
            const nx = px + ax;
            if (nx < 0 || nx >= width)
                continue;
            if (nx < bx0 || nx > bx1)
                continue;
            if (clip !== undefined && (nx < clip.x0 || nx > clip.x1))
                continue; // pixel outside the write clip
            if (maskActive) {
                const mi = (layerRow + nx) * 4;
                // Java 互換 mask compare (D'' rule, mask-java-compat-2026-05-21.md §3):
                //   - 通常マスク: stroke 開始時の snapshot を read (= Java の「stroke
                //     開始時の状態を mask 判定基準とする」自己保護機構を模倣)
                //   - 逆マスク : main を per-stamp で直読み (= Java の自己破壊機構
                //     を保持、α<FF で「ボツボツ」になる本質)
                //   - 共通    : α=0 (= 透明 = 空キャンバス or 完全消し跡) を CANVAS_BG_RGB
                //     (= 0xFFFFFF 白) で置換してから比較 (= Java の白 α=255 初期化モデル
                //     を Node 透明モデルに翻訳). 旧式の「α=0 を一致から除外する」消しゴム
                //     跡対策最適化と同じ結果になる (= 黒マスク + 黒線消し跡 → 白扱い →
                //     黒 ≠ 白 → 描ける).
                // 通常マスクで `maskSnapshot` が未指定なときは `mainRgba` を fallback
                // として使う (= 直 rasterizer 呼び出しテスト用、StrokeRenderer 経由なら
                // 必ず set される). 逆マスクは常に `mainRgba` を直読み.
                const src = maskInverse ? mainRgba : (maskSnapshot ?? mainRgba);
                const a = src[mi + 3] ?? 0;
                let rgb;
                if (a === 0) {
                    rgb = CANVAS_BG_RGB;
                }
                else {
                    const r = src[mi];
                    const g = src[mi + 1];
                    const b = src[mi + 2];
                    if (r === undefined || g === undefined || b === undefined)
                        continue;
                    rgb = (r << 16) | (g << 8) | b;
                }
                const matches = (maskColors[0] !== undefined && maskColors[0] === rgb) ||
                    (maskColors[1] !== undefined && maskColors[1] === rgb);
                // normal mask = paint where it does NOT match; inverse = where it does.
                if (matches !== maskInverse)
                    continue;
            }
            // Texture multiply (texture-brush-design.md §2): fold the per-texel
            // alpha `t ∈ [0,1]` into the per-stamp value **at the single
            // quantisation point** (= `round(penAlpha * t * 256)`), so texture is a
            // per-pixel axis and the determinism rule (one `round`) is preserved.
            // `texture === undefined` (all non-texture pens) reuses the hoisted
            // `penAlpha16` → byte-for-byte unchanged.
            let s = penAlpha16;
            if (texture !== undefined) {
                // `sampleTexel` picks world vs stamp mapping; stamp mapping rotates the
                // local coord by the precomputed `textureDir` (= stroke direction, a
                // pure function of the segment → deterministic). Centre = the stamp's
                // float position `(x, y)`.
                const t = sampleTexel(texture, nx, ny, x, y, textureDir);
                if (textureThreshold) {
                    // Screentone threshold-dither (texture-brush-design.md §12.2): `t` is
                    // a per-texel THRESHOLD. Ink iff `threshold < densityLevel`, at the
                    // FULL per-stamp alpha (no `t/255` multiply). No new quantisation —
                    // `penAlpha16` is the same hoisted value the non-texture path uses.
                    //
                    // Crayon-only edge falloff (§2.5.2 "② 縁のほつれ",
                    // `crayonEdgeDensityLevelAt`'s doc): the comparison level itself is
                    // scaled down near the stamp edge, BEFORE the ink test. Only
                    // `createCrayonStampRenderer` sets `textureThresholdEdgeFalloff`
                    // (§4.11 point 3 — tone's AA soft edge does NOT), so
                    // `effDensityLevel` stays exactly `densityLevel` for tone — byte-
                    // for-byte unchanged (both with `aa=false` AND `aa=true`).
                    let effDensityLevel = densityLevel;
                    if (textureThresholdEdgeFalloff) {
                        const dx = nx + 0.5 - x;
                        const dy = ny + 0.5 - y;
                        effDensityLevel = crayonEdgeDensityLevelAt(dx, dy, crayonRadius, densityLevel);
                    }
                    if (t >= effDensityLevel)
                        continue;
                    // Soft edge (phase11-light-pens-2026-08-21.md §2.5.1 crayon / §4.11
                    // tone-AA, `StampParams.textureThresholdSoft` doc). Tone with
                    // `aa=false` never sets this flag, so this whole branch is dead for
                    // it and `s` stays the hoisted `penAlpha16` exactly as before —
                    // byte-for-byte unchanged.
                    if (textureThresholdSoft) {
                        const margin = effDensityLevel - t;
                        if (margin < textureThresholdSoftEdgeWidth) {
                            s = Math.round(penAlpha * (margin / textureThresholdSoftEdgeWidth) * 256);
                            if (s === 0)
                                continue;
                        }
                    }
                }
                else {
                    // Alpha-multiply (texture presets): fold `t` into the SINGLE
                    // quantisation point.
                    s = Math.round(penAlpha * (t / 255) * 256);
                    if (s === 0)
                        continue;
                }
            }
            else if (airbrush !== undefined) {
                // Airbrush ink decision (airbrush-design-2026-08-15.md §1/§2, scatter
                // revision 2026-08-20 §1.3 — see `airbrushIsInk`'s doc): grain=0 is a
                // per-pixel stateless-hash threshold dither; grain>=1 is a 3×3
                // neighbourhood of round, irregularly-jittered droplets (replaces the
                // old world-grid cell-quantised kernel, which produced axis-aligned
                // mosaic squares). Either way, ink is at the FULL per-stamp alpha
                // (`s` stays the hoisted `penAlpha16` — no multiply, same shape as
                // the tone threshold path above).
                if (!airbrushIsInkFast(airbrush, stampIndex, x, y, airbrushRadius, nx, ny))
                    continue;
            }
            const idx = bufRow + (nx - bx0);
            const cur = alpha16[idx] ?? 0;
            if (accumulate === 'add') {
                // Source-over (漸近合成): `out = in + s * (1 - in)` in 0..65535.
                // The shift-by-16 approximates `/65536` rather than `/65535` —
                // max error ≈ 1 part in 65535, invisible at 8-bit final output.
                // Saturating clamp at 65535 because `cur=65534` + `add=65535*1`
                // can briefly overshoot (pathological alpha=255 + integer
                // rounding).
                const remaining = 65535 - cur;
                const add = (s * remaining) >> 16;
                const next = cur + add;
                alpha16[idx] = next > 65535 ? 65535 : next;
            }
            else {
                // 'max' — flat pen wants uniform per-pixel buffer alpha. Repeated
                // stamps on the same pixel must NOT pile up, otherwise the single
                // end-of-stroke commit shows visible build-up (looks like brush),
                // contradicting §19.3 ("ストローク内で往復しても uniform").
                if (s > cur)
                    alpha16[idx] = s;
            }
            if (buffer.dirty.x0 < 0 || nx < buffer.dirty.x0)
                buffer.dirty.x0 = nx;
            if (buffer.dirty.y0 < 0 || ny < buffer.dirty.y0)
                buffer.dirty.y0 = ny;
            if (nx > buffer.dirty.x1)
                buffer.dirty.x1 = nx;
            if (ny > buffer.dirty.y1)
                buffer.dirty.y1 = ny;
        }
    }
};
/**
 * Anti-aliased stamp (modern-layout.md §9.9, consult 2026-06-08).
 *
 * Analytic SDF (signed-distance) coverage path. Unlike `stampOnce` — which
 * snaps the center with `Math.floor` and walks a **binary** disc mask from
 * `penPattern.ts` — this computes a **fractional** edge coverage per pixel
 * from the *unrounded* float center `(x, y)` and *unrounded* float radius
 * `r = scaledWidthRaw / 2`:
 *
 *   for each footprint pixel center (ix+0.5, iy+0.5):
 *     dx = (ix + 0.5) - x
 *     dy = (iy + 0.5) - y
 *     d  = sqrt(dx*dx + dy*dy)
 *     cov = clamp(r + 0.5 - d, 0, 1)      // 1px-wide linear edge ramp
 *
 * The two AA wins (§9.9 決定1/2):
 *   (a) edge: `cov` is fractional on the disc boundary → no 0/1 hard edge.
 *   (b) subpixel position: we never `floor` the center → an axis-diagonal
 *       1px line distributes across 2 pixels (smooth), while an axis-aligned
 *       1px line at an integer position stays `cov=1` (crisp, not blurred).
 *
 * Determinism (§9.9 決定論ルール, ABSOLUTE): coverage uses ONLY
 * `+ - * / sqrt` and clamp/round — **no `pow/exp/sin/hypot`** (we expand
 * `sqrt(dx*dx + dy*dy)` instead of `Math.hypot`). `+ - * / sqrt` are IEEE-754
 * correctly-rounded and bit-identical across V8 platforms, so the shared TS
 * core produces identical bytes on the web client and the server snapshot
 * worker (`SnapshotEngine` runs this same `EngineCore`). The coverage→alpha
 * quantisation is done in **exactly one place**: `round(penAlpha * cov * 256)`.
 *
 * Footprint padding: the edge ramp extends ~0.5px beyond `r`, so the
 * footprint is padded by +1px on every side vs the geometric radius. The
 * bbox we `ensureBboxCovers` must include that padding (else a partly-covered
 * edge pixel would be dropped) — `stampFootprintBbox` already adds `+1` to
 * `half`, and we additionally pad the per-pixel iteration window by 1.
 *
 * Accumulation (§9.3 決定3) matches `stampOnce` exactly so AA composites the
 * same way the binary path does:
 *   'add' (brush, source-over): `out = in + ((s * (65535 - in)) >> 16)` with
 *          `s = round(penAlpha * cov * 256)`. 1px-spaced overlap converges
 *          toward full alpha; the disc edge keeps partial coverage.
 *   'max'  (flat, マスク基盤): `alpha16[i] = max(cur, round(penAlpha*cov*256))`.
 *          Interior pixels reach `round(penAlpha*256)` (uniform, 往復で
 *          濃くならない, §19.3 維持); only the edge keeps partial coverage.
 *
 * Mask compare (normal/inverse, D'' rule) is **identical** to `stampOnce`.
 * Eraser / mask 自己破壊 (scallop) は AA 対象外 — they never reach this path.
 */
const stampOnceAA = (p) => {
    const { buffer, mainRgba, maskSnapshot, width, height, x, y, mask, texture, airbrush, clip } = p;
    const textureDir = p.textureDir ?? NO_DIRECTION;
    const textureThreshold = p.textureThreshold === true;
    const textureThresholdSoft = p.textureThresholdSoft === true;
    const textureThresholdSoftEdgeWidth = p.textureThresholdSoftEdgeWidth ?? CRAYON_THRESHOLD_SOFT_EDGE_WIDTH;
    const textureThresholdEdgeFalloff = p.textureThresholdEdgeFalloff === true;
    const densityLevel = p.densityLevel ?? 0;
    const stampIndex = p.stampIndex ?? 0;
    const accumulate = p.accumulate ?? 'add';
    const widthScale = p.widthScale ?? 1;
    const alphaScale = p.alphaScale ?? 1;
    // Float radius — NOT rounded (= 太さ遷移も滑らか、§9.9 決定1). The legacy
    // path rounds width to an integer pen pattern; AA keeps the true radius so
    // sub-integer width changes ramp continuously.
    const scaledWidthRaw = p.penWidth * widthScale;
    const rRaw = scaledWidthRaw / 2;
    // Guard against a zero/negative radius (pressure=0 width). A 1px line wants
    // a minimum radius so it still deposits; mirror `stampOnce`'s width≥1 floor
    // by clamping the radius to half a pixel.
    const r = rRaw < 0.5 ? 0.5 : rRaw;
    const scaledAlphaRaw = p.penAlpha * alphaScale;
    const penAlpha = scaledAlphaRaw <= 0 ? 0 : scaledAlphaRaw >= 255 ? 255 : scaledAlphaRaw;
    if (penAlpha <= 0)
        return;
    // Footprint: the geometric disc spans `[x-r, x+r]`; the linear edge ramp
    // extends another ~0.5px, and we pad by a full +1px on each side so no
    // partially-covered edge pixel is missed. Iterate integer pixel rows/cols
    // whose center could have non-zero coverage.
    const ix0 = Math.floor(x - r - 1);
    const ix1 = Math.ceil(x + r + 1);
    const iy0 = Math.floor(y - r - 1);
    const iy1 = Math.ceil(y + r + 1);
    // Ensure the buffer bbox covers the padded footprint. `stampFootprintBbox`
    // computes `half = ceil(penWidth/2)+1`; with the +1 it already covers the
    // ramp for the integer-rounded width, and `ensureBboxCovers` clamps to the
    // layer. We feed the unrounded `scaledWidthRaw` so the disc radius used for
    // the bbox matches the radius used for coverage.
    const need = stampFootprintBbox(x, y, scaledWidthRaw, width, height);
    if (need.x0 > need.x1 || need.y0 > need.y1)
        return; // entirely off-canvas
    // Undo B' write clip (see `StampParams.clip` / `stampOnce`'s matching
    // early-out): skip the whole stamp before any buffer growth if it can't
    // possibly touch `clip`.
    if (clip !== undefined && !bboxOverlaps(need, clip))
        return;
    ensureBboxCovers(buffer, need);
    const { x0: bx0, y0: by0, x1: bx1, y1: by1 } = buffer.bbox;
    const bboxW = bx1 - bx0 + 1;
    const alpha16 = buffer.alpha16;
    const maskActive = mask.type !== 'none';
    const maskColors = mask.colors;
    const maskInverse = mask.type === 'inverse';
    // Clamp iteration to the layer AND the buffer bbox (defensive: bbox padding
    // and layer clamping can disagree on the very edge). The Undo B' write
    // clip folds into the SAME clamp (zero extra per-pixel branch cost): the
    // AA path already iterates an explicit window rather than a fixed pattern
    // grid, so narrowing that window is the write-gate.
    const startY = Math.max(iy0, 0, by0, clip !== undefined ? clip.y0 : Number.NEGATIVE_INFINITY);
    const endY = Math.min(iy1, height - 1, by1, clip !== undefined ? clip.y1 : Number.POSITIVE_INFINITY);
    const startX = Math.max(ix0, 0, bx0, clip !== undefined ? clip.x0 : Number.NEGATIVE_INFINITY);
    const endX = Math.min(ix1, width - 1, bx1, clip !== undefined ? clip.x1 : Number.POSITIVE_INFINITY);
    // Airbrush scatter kernel (grain>=1) perf — see `stampOnce`'s matching
    // block for the full rationale. `[startX,endX] x [startY,endY]` above is
    // already the exact pixel window this loop iterates, so the cache range is
    // just that window ± the 1-cell neighbour margin.
    const airbrushScatter = airbrush !== undefined && airbrush.grain > 0;
    if (airbrushScatter) {
        const grain = airbrush.grain;
        if (startY <= endY && startX <= endX) {
            prepareAirbrushScatterTable(airbrush, stampIndex, x, y, r, (startX >> grain) - 1, (startY >> grain) - 1, (endX >> grain) + 1, (endY >> grain) + 1);
        }
    }
    for (let iy = startY; iy <= endY; iy++) {
        const dy = iy + 0.5 - y;
        const dy2 = dy * dy;
        const layerRow = iy * width;
        const bufRow = (iy - by0) * bboxW;
        for (let ix = startX; ix <= endX; ix++) {
            const dx = ix + 0.5 - x;
            // Analytic coverage: 1px-wide linear ramp on the disc edge. ONLY uses
            // `sqrt` + `* + -` + clamp — no transcendentals (§9.9 決定論ルール 1).
            const d = Math.sqrt(dx * dx + dy2);
            let cov = r + 0.5 - d;
            if (cov <= 0)
                continue; // fully outside the (padded) disc
            if (cov > 1)
                cov = 1; // fully inside → crisp interior
            if (maskActive) {
                const mi = (layerRow + ix) * 4;
                // Identical D'' mask compare to `stampOnce` (mask-java-compat §3):
                // normal mask reads the stroke-start snapshot, inverse reads live
                // main; α=0 cells are treated as CANVAS_BG_RGB (white).
                const src = maskInverse ? mainRgba : (maskSnapshot ?? mainRgba);
                const a = src[mi + 3] ?? 0;
                let rgb;
                if (a === 0) {
                    rgb = CANVAS_BG_RGB;
                }
                else {
                    const sr = src[mi];
                    const sg = src[mi + 1];
                    const sb = src[mi + 2];
                    if (sr === undefined || sg === undefined || sb === undefined)
                        continue;
                    rgb = (sr << 16) | (sg << 8) | sb;
                }
                const matches = (maskColors[0] !== undefined && maskColors[0] === rgb) ||
                    (maskColors[1] !== undefined && maskColors[1] === rgb);
                if (matches !== maskInverse)
                    continue;
            }
            // Single quantisation point (§9.9 決定論ルール 2 / texture-brush-design.md
            // §2): per-stamp value in 8.8 fixed-point. Texture multiplies the
            // per-texel alpha `t ∈ [0,1]` into the SAME `round` so coverage × texture
            // composite stays a single quantisation. `texture === undefined` keeps the
            // pre-texture math byte-for-byte (round(penAlpha * cov * 256)).
            let s;
            if (texture !== undefined && textureThreshold) {
                // Screentone threshold-dither (texture-brush-design.md §12.2): `t` is a
                // per-texel threshold. Ink iff `threshold < densityLevel`, at the
                // coverage-scaled per-stamp alpha (= the SAME single quantisation as the
                // no-texture AA path, `round(penAlpha * cov * 256)`). No `t/255` multiply.
                const t = sampleTexel(texture, ix, iy, x, y, textureDir);
                // Crayon-only edge falloff (§2.5.2 "② 縁のほつれ",
                // `crayonEdgeDensityLevelAt`'s doc): reuses `r`, the SAME unrounded
                // radius the coverage ramp above already computed. Only
                // `createCrayonStampRenderer` sets `textureThresholdEdgeFalloff`
                // (§4.11 point 3 — tone's AA soft edge does NOT), so
                // `effDensityLevel` stays exactly `densityLevel` for tone — byte-
                // for-byte unchanged.
                const effDensityLevel = textureThresholdEdgeFalloff
                    ? crayonEdgeDensityLevelAt(dx, dy, r, densityLevel)
                    : densityLevel;
                if (t >= effDensityLevel)
                    continue;
                // Soft edge (phase11-light-pens-2026-08-21.md §2.5.1 crayon / §4.11
                // tone-AA, `StampParams.textureThresholdSoft` doc). Tone with
                // `aa=false` never sets this flag, so `s` always falls to the `else`
                // below for it — byte-for-byte unchanged. The shared
                // `if (s === 0) continue;` further down covers the case a
                // soft-scaled `s` rounds to 0 (no separate check needed here, unlike
                // the Classic `stampOnce` path).
                if (textureThresholdSoft) {
                    const margin = effDensityLevel - t;
                    if (margin < textureThresholdSoftEdgeWidth) {
                        s = Math.round(penAlpha * cov * (margin / textureThresholdSoftEdgeWidth) * 256);
                    }
                    else {
                        s = Math.round(penAlpha * cov * 256);
                    }
                }
                else {
                    s = Math.round(penAlpha * cov * 256);
                }
            }
            else if (texture !== undefined) {
                // Stamp centre = the AA stamp's float position `(x, y)`; world mapping
                // ignores it. `textureDir` rotates stamp-mapped textures (= 毛先 etc.).
                const t = sampleTexel(texture, ix, iy, x, y, textureDir);
                if (t === 0)
                    continue;
                s = Math.round(penAlpha * cov * (t / 255) * 256);
            }
            else if (airbrush !== undefined) {
                // airbrush-design-2026-08-15.md §1/§2, scatter revision 2026-08-20
                // §1.3: same ink decision as `stamp_once` (`airbrushIsInk`), but `s`
                // uses the AA coverage-scaled per-stamp value on ink (= the SAME
                // single quantisation the no-texture AA path uses).
                if (!airbrushIsInkFast(airbrush, stampIndex, x, y, r, ix, iy))
                    continue;
                s = Math.round(penAlpha * cov * 256);
            }
            else {
                s = Math.round(penAlpha * cov * 256);
            }
            if (s === 0)
                continue;
            const idx = bufRow + (ix - bx0);
            const cur = alpha16[idx] ?? 0;
            if (accumulate === 'add') {
                // Source-over, identical math to `stampOnce`'s 'add' branch.
                const remaining = 65535 - cur;
                const add = (s * remaining) >> 16;
                const next = cur + add;
                alpha16[idx] = next > 65535 ? 65535 : next;
            }
            else {
                // 'max' — uniform interior + partial edge (§19.3 維持).
                if (s > cur)
                    alpha16[idx] = s;
            }
            if (buffer.dirty.x0 < 0 || ix < buffer.dirty.x0)
                buffer.dirty.x0 = ix;
            if (buffer.dirty.y0 < 0 || iy < buffer.dirty.y0)
                buffer.dirty.y0 = iy;
            if (ix > buffer.dirty.x1)
                buffer.dirty.x1 = ix;
            if (iy > buffer.dirty.y1)
                buffer.dirty.y1 = iy;
        }
    }
};
/**
 * Sweep stamps along a line segment, exactly as `drawLineReal` does, so a
 * fast pen on a long line still produces a continuous trail.
 *
 * **Half-open endpoint convention** (M1 fix for "始点が突出して濃い"):
 * stamps `i = 1..steps` only — i.e. the start endpoint `(x1, y1)` is
 * **NOT** stamped, only the destination `(x2, y2)` and the in-between
 * sweep. The caller is responsible for stamping the very first point of
 * a stroke separately (`drawDot`); subsequent segments rely on the
 * previous segment's end-stamp covering the shared boundary point.
 *
 * Rationale: the previous "stamp both ends" behaviour double-stamped
 * every PointerEvent coordinate (each interior point was hit once as the
 * end of segment N and once as the start of segment N+1; the first point
 * was hit by `drawDot` and again by the start of segment 1). The
 * source-over buffer turns that into visible darker pips at every sample
 * point (similar to the old saturating-add behaviour), most obvious on
 * `brush` with mid-range alpha (low per-stamp alpha + build-up). With
 * the half-open convention every coord is stamped exactly once.
 */
export const drawLine = (p) => {
    // Per-call `performance.measure` instrumentation, gated by the module
    // flag in `perfMeasure.ts`. Default OFF (= single boolean check pass-
    // through). The e2e harness flips it ON via `enableDrawingPerfMeasure()`
    // before driving strokes so `performance.getEntriesByType('measure')`
    // aggregates per-segment wall-clock per pen kind.
    // (wide-eraser-perf-investigation-2026-05-16.md §「第 2 ラウンド」)
    measureDrawingHotPath(DRAWING_PERF_MEASURE_NAMES.drawLine, () => drawLineImpl(p));
};
const drawLineImpl = (p) => {
    const dxAbs = Math.abs(p.x2 - p.x1);
    const dyAbs = Math.abs(p.y2 - p.y1);
    const steps = Math.max(1, Math.ceil(Math.max(dxAbs, dyAbs)));
    // Pressure scales: linearly interpolate between segment endpoints so a
    // slow stroke whose pressure changes mid-sweep produces a smooth taper
    // (matches CSP/SAI behaviour). Default-1 endpoints mean "no pressure
    // applied" and the interpolation collapses to a constant 1, restoring
    // the pre-pressure path bit-for-bit.
    const wScaleStart = p.widthScaleStart ?? 1;
    const wScaleEnd = p.widthScaleEnd ?? 1;
    const aScaleStart = p.alphaScaleStart ?? 1;
    const aScaleEnd = p.alphaScaleEnd ?? 1;
    // Stroke direction for stamp-mapped textures (毛先/スプレー/雲). Derived ONCE
    // from the segment endpoints (deterministic: only sqrt + * + - /) and shared
    // by every stamp along the sweep, so the毛筋 follow the brush. An explicit
    // `p.textureDir` override wins (= future pens). World-mapped textures and all
    // non-texture pens ignore it. (texture-brush-design.md §1 / §6)
    const textureDir = p.textureDir ?? directionFromSegment(p.x1, p.y1, p.x2, p.y2);
    const stampCommon = {
        buffer: p.buffer,
        mainRgba: p.mainRgba,
        maskSnapshot: p.maskSnapshot,
        width: p.width,
        height: p.height,
        penAlpha: p.penAlpha,
        penWidth: p.penWidth,
        mask: p.mask,
        accumulate: p.accumulate,
        aa: p.aa,
        texture: p.texture,
        textureDir,
        textureThreshold: p.textureThreshold,
        densityLevel: p.densityLevel,
        textureThresholdSoft: p.textureThresholdSoft,
        textureThresholdSoftEdgeWidth: p.textureThresholdSoftEdgeWidth,
        textureThresholdEdgeFalloff: p.textureThresholdEdgeFalloff,
        airbrush: p.airbrush,
        clip: p.clip,
    };
    // i starts at 1 — see header comment. End endpoint (i=steps) is included.
    // `stampIndex: i` is airbrush's per-sweep-step hash input (§1) — every
    // OTHER pen ignores it (only read when `airbrush` is set).
    for (let i = 1; i <= steps; i++) {
        const t = i / steps;
        const x = p.x1 + (p.x2 - p.x1) * t;
        const y = p.y1 + (p.y2 - p.y1) * t;
        const widthScale = wScaleStart + (wScaleEnd - wScaleStart) * t;
        const alphaScale = aScaleStart + (aScaleEnd - aScaleStart) * t;
        stampOnce({ ...stampCommon, x, y, widthScale, alphaScale, stampIndex: i });
    }
};
/**
 * Single-point convenience for "click without drag".
 *
 * Unlike `drawLine` (which interpolates between two pressure endpoints), a
 * dot has just one pressure value. We accept it under the `widthScaleEnd`
 * / `alphaScaleEnd` field name (the "destination" of a zero-length
 * sweep), defaulting to `widthScaleStart` / `alphaScaleStart` if only the
 * start was supplied — that way the renderer can pass through whichever
 * endpoint is most natural at the call site.
 */
export const drawDot = (p) => {
    const widthScale = p.widthScaleEnd ?? p.widthScaleStart ?? 1;
    const alphaScale = p.alphaScaleEnd ?? p.alphaScaleStart ?? 1;
    stampOnce({
        buffer: p.buffer,
        mainRgba: p.mainRgba,
        maskSnapshot: p.maskSnapshot,
        width: p.width,
        height: p.height,
        x: p.x1,
        y: p.y1,
        penAlpha: p.penAlpha,
        penWidth: p.penWidth,
        mask: p.mask,
        accumulate: p.accumulate,
        aa: p.aa,
        texture: p.texture,
        // A dot has no direction → θ=0 (no rotation). Stamp-mapped textures still
        // sample, just un-rotated (texture-brush-design.md §1 "dot() → θ=0").
        textureDir: p.textureDir ?? NO_DIRECTION,
        textureThreshold: p.textureThreshold,
        densityLevel: p.densityLevel,
        textureThresholdSoft: p.textureThresholdSoft,
        textureThresholdSoftEdgeWidth: p.textureThresholdSoftEdgeWidth,
        textureThresholdEdgeFalloff: p.textureThresholdEdgeFalloff,
        airbrush: p.airbrush,
        // `drawDot` has no sweep (exactly one stamp) → `stampIndex` stays at the
        // `StampParams` default (0) — see `StampParams.stampIndex` doc.
        clip: p.clip,
        widthScale,
        alphaScale,
    });
};
/**
 * Composite the stroke buffer onto the layer's main bitmap with a single
 * alpha-blend pass over the buffer's dirty bbox.
 *
 * Per architecture.md §19.1: this runs once per `commit point`, never
 * stamp-by-stamp, so cumulative blend artifacts cannot occur.
 *
 * 2026-05-09 (consult / brush-alpha-cap-investigation.md §4-I): the
 * buffer is Uint16 (8.8 fixed-point) over a bbox-local sub-rect. Commit
 * iterates the **dirty** bbox (not the larger allocation bbox), converts
 * each touched cell to an 8-bit alpha (`>>8` clamped 0..255), and applies
 * the standard source-over alpha-blend onto the layer's RGBA bytes.
 * The Uint16 → Uint8 conversion is the only place the precision drop
 * happens; until commit the per-stamp accumulation runs at the higher
 * precision.
 */
export const commitBufferToLayer = (p) => {
    const { layer, buffer, color, userAlpha, commitTexture } = p;
    const { x0: dx0, y0: dy0, x1: dx1, y1: dy1 } = buffer.dirty;
    if (dx0 < 0 || userAlpha <= 0)
        return;
    const { x0: bx0, y0: by0, x1: bx1 } = buffer.bbox;
    if (bx0 < 0)
        return; // defensive: dirty is set but bbox isn't (shouldn't happen)
    const bboxW = bx1 - bx0 + 1;
    const r = (color >> 16) & 0xff;
    const g = (color >> 8) & 0xff;
    const b = color & 0xff;
    const w = layer.width;
    const alpha16 = buffer.alpha16;
    for (let y = dy0; y <= dy1; y++) {
        const bufRow = (y - by0) * bboxW;
        const rowRgba = y * w * 4;
        for (let x = dx0; x <= dx1; x++) {
            const a16 = alpha16[bufRow + (x - bx0)] ?? 0;
            if (a16 === 0)
                continue;
            // Decimate 8.8 fixed-point → 8-bit alpha. Round-to-nearest with
            // saturating clamp at 255.
            const a8raw = (a16 + 128) >> 8;
            let a = a8raw > 255 ? 255 : a8raw;
            // Commit-time texture multiply (§2.5.5 doc on `CommitParams.commitTexture`).
            // `undefined` for every pen except pastel — skipped entirely, byte-for-byte
            // unchanged.
            if (commitTexture !== undefined) {
                const t = sampleTextureAlpha(commitTexture, x, y);
                a = Math.round((a * t) / 255);
                if (a === 0)
                    continue;
            }
            // Effective stamp alpha = bufferAlpha * userAlpha (both 0..255).
            // Standard "source over" on premul-ish RGBA8.
            const srcA = (a * userAlpha) / 255 / 255; // 0..1
            const dst = rowRgba + x * 4;
            const dr = layer.main[dst] ?? 0;
            const dg = layer.main[dst + 1] ?? 0;
            const db = layer.main[dst + 2] ?? 0;
            const da = (layer.main[dst + 3] ?? 0) / 255;
            const outA = srcA + da * (1 - srcA);
            if (outA <= 0)
                continue;
            layer.main[dst] = (r * srcA + dr * da * (1 - srcA)) / outA;
            layer.main[dst + 1] = (g * srcA + dg * da * (1 - srcA)) / outA;
            layer.main[dst + 2] = (b * srcA + db * da * (1 - srcA)) / outA;
            layer.main[dst + 3] = Math.round(outA * 255);
        }
    }
};
/**
 * Sub-rect variant of `commitBufferToLayer` used by the per-rAF display
 * compositor (PR 2-α 2026-05-17 — `compositeForDisplay` の dirty-rect 化).
 *
 * Same source-over blend math as `commitBufferToLayer`, but writes into a
 * caller-supplied `target` RGBA sub-buffer instead of a `Layer.main`. The
 * sub-buffer represents a `subWidth × subHeight` rectangle whose top-left
 * sits at layer-space `(subX0, subY0)`; only pixels of the buffer's dirty
 * bbox that fall inside that sub-rect are blended (others are silently
 * skipped — they'll be picked up by a later `flush` if needed).
 *
 * The split exists so the heavy per-rAF compositor path (= 4000²-canvas
 * `flat`/`marker` deferred-commit replay) allocates and iterates **only**
 * the dirty bbox region instead of the whole canvas. `commitBufferToLayer`
 * stays unchanged so the snapshot worker / per-segment brush commits keep
 * their existing well-tested path (architecture.md §19.1 / §19.9).
 *
 * The numeric blend is bit-identical to `commitBufferToLayer` for pixels
 * inside the sub-rect — the only difference is the destination indexing.
 */
export const commitBufferToSubrect = (p) => {
    const { buffer, color, userAlpha, target, subX0, subY0, subWidth, subHeight, commitTexture } = p;
    if (userAlpha <= 0)
        return;
    const { x0: dx0, y0: dy0, x1: dx1, y1: dy1 } = buffer.dirty;
    if (dx0 < 0)
        return;
    const { x0: bx0, y0: by0, x1: bx1 } = buffer.bbox;
    if (bx0 < 0)
        return;
    // Clip the buffer's dirty bbox to the destination sub-rect's layer-space
    // window. Pixels outside the sub-rect are skipped (no overdraw onto
    // unrelated cells of `target`).
    const cx0 = dx0 > subX0 ? dx0 : subX0;
    const cy0 = dy0 > subY0 ? dy0 : subY0;
    const subX1 = subX0 + subWidth - 1;
    const subY1 = subY0 + subHeight - 1;
    const cx1 = dx1 < subX1 ? dx1 : subX1;
    const cy1 = dy1 < subY1 ? dy1 : subY1;
    if (cx0 > cx1 || cy0 > cy1)
        return; // no intersection
    const bboxW = bx1 - bx0 + 1;
    const r = (color >> 16) & 0xff;
    const g = (color >> 8) & 0xff;
    const b = color & 0xff;
    const alpha16 = buffer.alpha16;
    for (let y = cy0; y <= cy1; y++) {
        const bufRow = (y - by0) * bboxW;
        const targetRow = (y - subY0) * subWidth * 4;
        for (let x = cx0; x <= cx1; x++) {
            const a16 = alpha16[bufRow + (x - bx0)] ?? 0;
            if (a16 === 0)
                continue;
            const a8raw = (a16 + 128) >> 8;
            let a = a8raw > 255 ? 255 : a8raw;
            if (commitTexture !== undefined) {
                const t = sampleTextureAlpha(commitTexture, x, y);
                a = Math.round((a * t) / 255);
                if (a === 0)
                    continue;
            }
            const srcA = (a * userAlpha) / 255 / 255;
            const dst = targetRow + (x - subX0) * 4;
            const dr = target[dst] ?? 0;
            const dg = target[dst + 1] ?? 0;
            const db = target[dst + 2] ?? 0;
            const da = (target[dst + 3] ?? 0) / 255;
            const outA = srcA + da * (1 - srcA);
            if (outA <= 0)
                continue;
            target[dst] = (r * srcA + dr * da * (1 - srcA)) / outA;
            target[dst + 1] = (g * srcA + dg * da * (1 - srcA)) / outA;
            target[dst + 2] = (b * srcA + db * da * (1 - srcA)) / outA;
            target[dst + 3] = Math.round(outA * 255);
        }
    }
};
/**
 * Erase pixels along a line by reducing the layer's alpha channel. Eraser
 * bypasses the buffer entirely (architecture.md §19.3 — color/alpha
 * ignored at render time, eraser is destination-out).
 */
export const eraseLine = (p) => {
    // See `drawLine` for the gated-measure rationale.
    measureDrawingHotPath(DRAWING_PERF_MEASURE_NAMES.eraseLine, () => eraseLineImpl(p));
};
const eraseLineImpl = (p) => {
    const dxAbs = Math.abs(p.x2 - p.x1);
    const dyAbs = Math.abs(p.y2 - p.y1);
    // 1px 毎の per-pixel sampling. 太い消しゴム経路で main thread が詰まる
    // problem は判明済 (wide-eraser-perf-investigation-2026-05-16.md §A) で、
    // T3 案 A (`ERASER_STEP_DIVISOR=2`) で粗化を試みたが視覚 regression
    // (= 円が scallop に割れる) のため 2026-05-17 第 2 ラウンド consult で
    // revert. 代替案は PR 2 で別途検討する.
    const steps = Math.max(1, Math.ceil(Math.max(dxAbs, dyAbs)));
    const wScaleStart = p.widthScaleStart ?? 1;
    const wScaleEnd = p.widthScaleEnd ?? 1;
    for (let i = 0; i <= steps; i++) {
        const t = steps === 0 ? 0 : i / steps;
        const x = p.x1 + (p.x2 - p.x1) * t;
        const y = p.y1 + (p.y2 - p.y1) * t;
        const widthScale = wScaleStart + (wScaleEnd - wScaleStart) * t;
        eraseDotInternal(p.layer, x, y, p.penWidth, p.strength, widthScale, p.mask, p.maskSnapshot, p.clip);
    }
};
export const eraseDot = (layer, x, y, penWidth, strength, widthScale = 1, mask = NO_MASK, maskSnapshot, 
/** Undo B' bbox-local repair write clip. See `StampParams.clip`. */
clip) => {
    eraseDotInternal(layer, x, y, penWidth, strength, widthScale, mask, maskSnapshot, clip);
};
/** Internal singleton for callers that don't need mask gating. */
const NO_MASK = { type: 'none', colors: [] };
const eraseDotInternal = (layer, x, y, penWidth, strength, widthScale, mask, maskSnapshot, clip) => {
    if (strength <= 0)
        return;
    // Apply pressure-derived width (architecture.md §19.15). Same min-1 floor
    // as `stampOnce` so a zero-pressure tip still erases at least one pixel.
    const scaledWidthRaw = penWidth * widthScale;
    const scaledWidth = scaledWidthRaw < 1 ? 1 : Math.round(scaledWidthRaw);
    const pattern = getPenPattern(scaledWidth);
    const half = (pattern.size - 1) / 2;
    const px = Math.floor(x - half);
    const py = Math.floor(y - half);
    // Undo B' write clip (see `StampParams.clip`): eraser writes `layer.main`
    // directly with no buffer/dirty-bbox bookkeeping to piggyback on, so this
    // whole-stamp early-out is the only thing that bounds an eraser replay's
    // cost during local repair.
    if (clip !== undefined) {
        const need = stampFootprintBbox(x, y, scaledWidth, layer.width, layer.height);
        if (need.x0 > need.x1 || need.y0 > need.y1)
            return; // entirely off-canvas
        if (!bboxOverlaps(need, clip))
            return;
    }
    const maskActive = mask.type !== 'none';
    const maskColors = mask.colors;
    const maskInverse = mask.type === 'inverse';
    for (let ay = 0; ay < pattern.size; ay++) {
        const ny = py + ay;
        if (ny < 0 || ny >= layer.height)
            continue;
        if (clip !== undefined && (ny < clip.y0 || ny > clip.y1))
            continue; // whole row outside the write clip
        const patRow = ay * pattern.size;
        for (let ax = 0; ax < pattern.size; ax++) {
            if (pattern.mask[patRow + ax] === 0)
                continue;
            const nx = px + ax;
            if (nx < 0 || nx >= layer.width)
                continue;
            if (clip !== undefined && (nx < clip.x0 || nx > clip.x1))
                continue; // pixel outside the write clip
            const idx = (ny * layer.width + nx) * 4;
            const a = layer.main[idx + 3] ?? 0;
            // 消しゴムにも mask gating を適用 (= stampOnce と同じ Java 互換 D'' rule,
            // mask-java-compat-2026-05-21.md §7.3). 通常マスクは stroke 開始時の
            // `maskSnapshot` を read, 逆マスクは `layer.main` を per-stamp 直読み.
            // 共通: α=0 → `CANVAS_BG_RGB` (= 白) で置換して比較.
            if (maskActive) {
                // For inverse mask, always read from live layer.main (= 自己破壊機構).
                // For normal mask, read from snapshot if supplied, else fall back to
                // layer.main (= eraseDot 直呼び出しテスト用).
                const src = maskInverse ? layer.main : (maskSnapshot ?? layer.main);
                const srcA = src === layer.main ? a : (src[idx + 3] ?? 0);
                let rgb;
                if (srcA === 0) {
                    rgb = CANVAS_BG_RGB;
                }
                else {
                    const r = src[idx];
                    const g = src[idx + 1];
                    const b = src[idx + 2];
                    if (r === undefined || g === undefined || b === undefined)
                        continue;
                    rgb = (r << 16) | (g << 8) | b;
                }
                const matches = (maskColors[0] !== undefined && maskColors[0] === rgb) ||
                    (maskColors[1] !== undefined && maskColors[1] === rgb);
                if (matches !== maskInverse)
                    continue;
            }
            const next = a - strength;
            const nextA = next < 0 ? 0 : next;
            layer.main[idx + 3] = nextA;
            // 旧 `c92e51d`: 完全消し時に RGB もクリアしていたが、layer/background 分離
            // (2026-05-17, docs/_wip/layer-background-separation-2026-05-17.md) で
            // 描画は **transparent-default の layer + 独立 bg** モデルに移行したため
            // 不要になった (= キャプ / スポイトとも `compositeForExport` 経由で bg
            // 合成された RGBA を読むので、α=0 域の layer.main RGB が黒のままでも
            // 視覚的には bg 色が出る). RGB を残しても `da=0` の commit pass では
            // `db * da * (1-srcA)` の寄与もゼロなので、上塗り時のブレンドにも混じら
            // ない. 余分な書き込みを省く分だけ太い消しゴム経路のスループットにも軽く
            // 効く (Phase 5 トレース観測ベース).
        }
    }
};
// ─── Pen → buffer alpha mapping ────────────────────────────────────────────
/**
 * Per-stamp alpha curve constants for `'brush'` (architecture.md §19.3,
 * consult 2026-05-09 / brush-alpha-cap-investigation.md §5).
 *
 *   per_stamp = K * pow(strokeAlpha / 255, gamma)   // float, 0..K
 *
 * Curve shape with `K=80, gamma=5`:
 *
 *   strokeAlpha=255 → 80     (1 stamp ≈ 31% opacity → many stamps → 100% ✅)
 *   strokeAlpha=200 → 24
 *   strokeAlpha=147 →  5.3   (中域、半透明維持 ✅ — ユーザー fb 期待挙動)
 *   strokeAlpha=128 →  2.7
 *   strokeAlpha=100 →  1.0
 *   strokeAlpha= 50 →  0.05  (床近傍、長い stroke で初めて見える)
 *
 * Combined with **source-over (漸近合成)** accumulation:
 *
 *   buffer[i] += per_stamp * (1 - buffer[i])
 *
 * the per-pixel alpha grows asymptotically toward 100% but never quite
 * reaches it within one stroke (= 厚塗り culture preserved). A fresh
 * stroke on top continues the build-up from where the previous left off,
 * so 同 stroke 内ぐりぐり vs 別 stroke 重ね描きの両方で coherent な濃化が出る.
 *
 * Verified numerics (太さ60, 60 stamp/pixel sweep, K=80, gamma=5):
 *
 *   alpha=255 → 1 stroke で 100% (= 不透明線画) ✅
 *   alpha=147 → 1 stroke で 71% (= 半透明) ✅
 *               ぐりぐり 2× で 92% (= 厚塗り build-up) ✅
 *               ぐりぐり 4× で 99% (= 漸近完全飽和) ✅
 *   alpha=100 → 1 stroke で 21% (= 薄め)
 *               ぐりぐり 4× で 62%
 *   alpha= 50 → 1 stroke で 1% (= 床近傍、長い stroke で初めて見える)
 *
 * **K**: starting value 80 — requires real-device tuning per the
 * brush-alpha-cap-investigation.md §5 残課題 #1. The const can be
 * overwritten and propagates bit-perfectly to all client/server because
 * drawing-core is shared.
 *
 * **gamma**: starting value 5, paired with K=80. Per §5 残課題 #2 the
 * combination requires re-evaluation if K changes — a smaller K may
 * permit a smaller gamma while keeping the alpha=255 → 1-stroke-opaque
 * property.
 */
export const BRUSH_PER_STAMP_ALPHA_K = 80;
export const BRUSH_PER_STAMP_ALPHA_GAMMA = 5;
/**
 * Airbrush per-stamp alpha scale (airbrush-design-2026-08-15.md §2.4). The
 * threshold kernel inks a pixel at the FULL per-stamp alpha (no `t/255`
 * multiply, §2.2) — so unlike `'brush'`'s power curve, there is no per-pixel
 * dial baked into the kernel to keep a single ink event translucent. If the
 * wire `strokeAlpha` were used unscaled, a palette-max stroke would ink every
 * hashed-in pixel at 100% on the FIRST pass (= "穴あきベタ", a hole-free solid
 * blob — the opposite of a spray). Scaling `strokeAlpha` down by a constant
 * keeps a single ink event clearly translucent; density then builds up over
 * MANY independent ink events (source-over `'add'`, §2.5) as the stroke
 * sweeps or is held in place (hold-to-build, Phase 3). Linear (no power
 * curve) — the build-up dynamic here comes from "how many times did this
 * pixel get hash-selected", not from a per-pixel opacity ramp.
 *
 * 0.35 is a starting value (real-machine tuning item per the design doc): at
 * wire alpha=255 a single ink event lands at `255 * 0.35 ≈ 89` (~35%
 * opacity), clearly translucent, while still visibly "inking" rather than
 * being lost in rounding.
 */
export const AIRBRUSH_PER_STAMP_ALPHA_SCALE = 0.35;
/**
 * The wire-level `alpha` (0..255) plays a different role per pen
 * (consult 2026-05-09 / architecture.md §19.3):
 *
 *   brush    : メインブラシ (旧 `'normal'` + `'water'` 統合). 不透明線画から
 *              build-up 半透明まで alpha 値で連続化. per-stamp alpha は
 *              **累乗カーブ `K * pow(strokeAlpha / 255, gamma)`** で計算
 *              (K = `BRUSH_PER_STAMP_ALPHA_K`, gamma = `BRUSH_PER_STAMP_ALPHA_GAMMA`).
 *              Returned as a float so the source-over accumulator can use
 *              the full 8.8 fixed-point precision (= 床問題回避).
 *              alpha=255 で 1 stroke ≈ 100% (多 stamp で漸近)、低 alpha で
 *              数十〜数百 stamp で漸近的に飽和する build-up.
 *              累乗化により旧実装の `/16` 縮小が不要になり、alpha=FF と
 *              alpha=FE の境界も連続的になる. (architecture.md §19.3)
 *   flat     : uniform alpha 塗り (旧 `'alpha'` から rename). per-stamp alpha
 *              = wire alpha. The engine pairs this with `accumulate: 'max'`
 *              so repeated stamps on the same pixel keep the buffer at
 *              exactly `strokeAlpha`, not `strokeAlpha × overlap`. Single
 *              end-of-stroke commit → uniform. マスク・逆マスクの基盤.
 *              (architecture.md §19.3)
 *   marker   : マーカー (consult 2026-05-09 試験追加). per-stamp alpha = wire
 *              alpha (= `'flat'` と同じ線形, 累乗カーブなし). 蓄積モードも
 *              `'max'` (= `'flat'` と同じ), commit タイミングも stroke 終端
 *              1 回 (= `'flat'` と同じ). `'flat'` との唯一の差は **per-stamp
 *              alpha が筆圧で動的変動する** こと (= `'flat'` は palette A 値で
 *              stroke 内 uniform、`'marker'` は per-stamp ごとに `stroke.alpha
 *              * pAlphaPressure` で変動). max 蓄積 + 動的 per-stamp の組合せ
 *              で「強筆圧域 = 平らな最大濃さ」「弱筆圧域 = 平らな弱濃さ」
 *              「中央⇔端が離散的」というキレのあるマーカー風筆致になる
 *              (= コピックマーカー風の独自表現、CSP / Photoshop に直接対応
 *              なし). 動的 per-stamp の混合は `strokeRenderer.processPoint`
 *              側で `pt.pAlpha` を `alphaScale` に乗せる経路で実現するので
 *              この関数の返値自体は `'flat'` と同一. (architecture.md §19.3)
 *   eraser   : not used (handled by `eraseLine`).
 *
 * Returning the per-stamp alpha here keeps the rasterizer pen-agnostic —
 * the engine picks the right number per pen kind, the rasterizer just
 * stamps with it. This is the seam where future pens (airbrush /
 * texture) plug in additional behaviour without touching the core.
 *
 * **Return type**: `number` in 0..255 (float for `'brush'`, integer for
 * `'flat'` / `'marker'`). The caller uses it as a per-stamp alpha that
 * gets multiplied by `widthScale`/`alphaScale` and quantised to 8.8
 * fixed-point inside the kernel. We deliberately return a float for
 * `'brush'` (no `Math.round` here) so the per-stamp curve isn't quantised
 * twice — only once at the source-over accumulation step.
 */
export const perStampAlphaFor = (pen, strokeAlpha) => {
    switch (pen) {
        case 'brush': {
            // 累乗カーブ build-up (consult 2026-05-09 / architecture.md §19.3).
            // Float return: source-over accumulator (Uint16 8.8 fixed-point)
            // takes the per-stamp alpha and quantises in `stampOnce`. Returning
            // float here means the curve preserves precision down to the 1/256
            // boundary (= no "床問題" where low strokeAlpha rounds to 0 or 1).
            //
            // 2026-05-16 fb 反映 (= α 直前の実機検証): strokeAlpha=255 (= FF、palette
            // 最大値) は **single-stamp で 100% opacity** を保証する "ink mode" として
            // 特例化する. 旧式 `K * 1^gamma = 80` は 1 stamp あたり ~31% で、細いペン
            // (= width=1〜2 で stamp 半径が小さく overlap が薄い) + 速い stroke では
            // pixel あたり 1-2 stamp しか乗らず「FF なのに色が薄い」体感バグが出ていた.
            // FF を special case で per_stamp = 256 (= source-over の 65535 cap に直達)
            // にすることで「palette 最大値 = solid line」のユーザー直感と整合.
            // FE 以下は累乗カーブをそのまま (= 中域グラデ + 厚塗り build-up を維持).
            if (strokeAlpha >= 255)
                return 256;
            const ratio = strokeAlpha / 255;
            const gamma = BRUSH_PER_STAMP_ALPHA_GAMMA;
            const K = BRUSH_PER_STAMP_ALPHA_K;
            return K * ratio ** gamma;
        }
        case 'flat':
            // uniform alpha 塗り (旧 `'alpha'`): per-stamp alpha is the wire alpha.
            // The engine pairs this with `accumulate: 'max'` so repeated stamps
            // on the same pixel keep the buffer at exactly `strokeAlpha`, not
            // `strokeAlpha × overlap`. Single end-of-stroke commit → uniform.
            // マスク・逆マスクの基盤. (architecture.md §19.3)
            return strokeAlpha;
        case 'marker':
            // マーカー (consult 2026-05-09 試験追加). per-stamp alpha は線形
            // (= wire alpha 額面、`'flat'` と同じ式). 累乗カーブは適用しない
            // (= `'brush'` と区別する本質). 動的変動は `strokeRenderer` 側で
            // `pt.pAlpha` を `alphaScale` に乗せる経路で実現. 蓄積モード `'max'` +
            // 動的 per-stamp の組合せで「stroke 内最大濃さで凍結」「ペン先入り
            // 抜きで離散的フェード」のマーカー風挙動が出る. (architecture.md §19.3)
            return strokeAlpha;
        case 'texture':
            // テクスチャブラシ (紙, texture-brush-design.md §2 / §6). The per-stamp
            // alpha curve is **identical to `'brush'`** (build-up source-over) so
            // 重ね塗りで紙目が揃い、下地が露出する挙動 (= 「紙」world mapping の本質)
            // が出る. The texture mask is applied as a separate per-pixel axis in
            // the kernel (`StampParams.texture`), NOT here — this function stays the
            // pen-agnostic per-stamp alpha provider (texture-brush-design.md §2
            // "widthScale / alphaScale とは別の per-pixel 軸").
            if (strokeAlpha >= 255)
                return 256;
            return BRUSH_PER_STAMP_ALPHA_K * (strokeAlpha / 255) ** BRUSH_PER_STAMP_ALPHA_GAMMA;
        case 'tone':
            // スクリーントーン (texture-brush-design.md §12.2): per-stamp alpha は線形
            // (= `'flat'` と同じ uniform). 網点の濃淡は density (= 閾値ディザの coverage)
            // で決まり、per-stamp alpha は inked texel の不透明度を一定に保つ. flat と
            // 同じ max 蓄積 + 遅延 commit で「重ね塗りで濃くならない均一トーン」が出る.
            return strokeAlpha;
        case 'airbrush':
            // エアブラシ (airbrush-design-2026-08-15.md §2.4). Linear (no power
            // curve) but scaled DOWN by a constant so a single ink event stays
            // translucent — see `AIRBRUSH_PER_STAMP_ALPHA_SCALE` doc for why. The
            // threshold decision itself (which pixels get inked at all) lives in
            // the kernel (`StampParams.airbrush`), NOT here — this function stays
            // the pen-agnostic per-stamp alpha provider.
            return strokeAlpha * AIRBRUSH_PER_STAMP_ALPHA_SCALE;
        case 'crayon':
            // クレヨン (phase11-light-pens-2026-08-21.md §2). Rides the SAME
            // threshold seam `'tone'` does, so it gets `'tone'`'s LINEAR per-stamp
            // alpha for the same reason (see that case's comment): the threshold
            // decision already governs WHICH pixels ink (§2.4's densityLevel), so
            // the per-stamp alpha here should be the plain full-strength value, not
            // the brush build-up power curve — a power curve would additionally
            // (and wrongly) suppress the opacity of texels that DID pass the
            // threshold, and at low `strokeAlpha` can round all the way to 0 (no
            // ink at all, even where the threshold says to ink).
            return strokeAlpha;
        case 'pastel':
            // パステル (同 §2.5.5, revised 2026-08-21 — "build-up を取り戻す").
            // The texture multiply moved OFF this per-dab path entirely (it now
            // happens once at commit — `StampRenderer.ts`'s `resolveCommitTexture` /
            // `rasterizer.ts`'s `commitBufferToLayer`'s `commitTexture` param), so
            // this per-dab kernel has no texel to protect from compounding anymore
            // — the §2.3 ⚠️ diagnosis that justified `'flat'`'s LINEAR curve no
            // longer applies here. `accumulateModeFor` (`strokeRenderer.ts`) pairs
            // pastel with `accumulate='add'` (build-up restored), same as `'brush'`.
            //
            // 2026-08-21 実機で LINEAR → `'brush'`/`'texture'` と同じ累乗カーブに変更。
            // LINEAR (per-stamp = strokeAlpha) は 1 スイープで同じ画素に ~width 回
            // ダブが乗るため **即座に飽和**し、1 ストローク内で往復しても濃さが変わら
            // なかった (= flat と同じ体感。作者から「brush みたいに重ね塗りにならない
            // のは仕様?」と指摘)。累乗カーブなら per-stamp が十分小さくなり、
            // alpha 120〜200 の実用域で **往復が明確に濃くなる**ことを実ストロークで確認。
            // alpha=255 は `'brush'` と同じ ink mode (= 一発で不透明)。
            // テクスチャは commit で 1 回だけ掛かるので、飽和しても粒は残る。
            if (strokeAlpha >= 255)
                return 256;
            return BRUSH_PER_STAMP_ALPHA_K * (strokeAlpha / 255) ** BRUSH_PER_STAMP_ALPHA_GAMMA;
        case 'dot':
            // ドット (同 §3). The stamps a dot pen deposits are plain brush stamps
            // (the decorator only changes WHICH sweep positions get a `dot()` call,
            // not the per-stamp alpha) — same build-up curve as `'brush'`.
            if (strokeAlpha >= 255)
                return 256;
            return BRUSH_PER_STAMP_ALPHA_K * (strokeAlpha / 255) ** BRUSH_PER_STAMP_ALPHA_GAMMA;
        case 'eraser':
            return 0; // not used
    }
};
// ─── Painting helpers ──────────────────────────────────────────────────────
/**
 * Paint a single layer with an opaque background color. Used at canvas
 * init / `whiteboard.clear` to mirror the legacy (transparent → opaque
 * white) startup state.
 */
export const fillLayer = (layer, color, alpha = 255) => {
    const r = (color >> 16) & 0xff;
    const g = (color >> 8) & 0xff;
    const b = color & 0xff;
    const buf = layer.main;
    for (let i = 0; i < buf.length; i += 4) {
        buf[i] = r;
        buf[i + 1] = g;
        buf[i + 2] = b;
        buf[i + 3] = alpha;
    }
};
//# sourceMappingURL=rasterizer.js.map